import moment from 'moment';
import * as Sentry from '@sentry/react';
import VError from 'verror';

import {
  NavigatorLanguages,
  QueryString,
  ReachTemplate,
  SentryManualError,
} from 'types';
import { JSONSchema6 } from 'json-schema';
import { BpitUtils } from 'services';
import { ADD_PARAM, RESOURCE_ID_FILTER } from 'appConstants';

interface HasType {
  type?: string;
}

const rxWhitespace = new RegExp(/\s+/g);

const MiscUtils = {
  /** Returns a boolean indicating if app is in a local dev environment */
  isLocalDev: window.location.hostname.includes('local.dev'),

  /** Returns a boolean indicating if app is in a production environment */
  isProduction:
    // Added localhost condition as 3 test cases were failing, we need to check why localhost is picked as hostname instead of local.dev
    !window.location.hostname.includes('local.dev') &&
    !window.location.hostname.includes('localhost') &&
    !window.location.hostname.includes('dev.'),

  /** Returns a boolean indicating if app is in a staging environment */
  isStaging: window.location.hostname.includes('staging.'),

  /** Returns sort method that sorts strings alphabetically, case-insensitive */
  alphabetize: (a: string | number, b: string | number) =>
    a.toString().toLowerCase() > b.toString().toLowerCase() ? 1 : -1,

  /** Returns string with first letter in uppercase */
  capitalize: (value: string) => value.charAt(0).toUpperCase() + value.slice(1),

  /** Create a curried event handler  that executes with supplied params */
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  curry: <T>(fn: Function, ...params: any[]) => (value: T) =>
    fn(...params, value),

  curryGet: <T>(field: string) => (object: T) => object[field],

  /** Returns nested value of object, evaluated at given path or a default */
  get: <T>(obj: object, path: string, defaultVal?: T): T => {
    const parts = path
      .replace(/\[/g, '.[')
      .split('.')
      .filter((part: string) => part !== '');

    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    let result: any = obj;

    while (parts.length && result) {
      let key: string | number = parts.shift() || '';

      if (key.startsWith('[')) {
        key = parseInt(key.slice(1, key.length - 1), 10);
      }

      result = result[key];
    }

    return result || defaultVal;
  },

  /**
   * Compares values of two similar objects and returns string array of
   * differing keys
   */
  getDiffering: (objA: {}, objB: {}) => {
    const keys = new Set(Object.keys(objA).concat(Object.keys(objB)));
    return Array.from(keys).filter((key) => objA[key] !== objB[key]);
  },

  getIndexOfMatch: (searchString: string, options: string[]): number => {
    for (const val of options) {
      let idx = searchString.indexOf(val);
      if (idx !== -1) {
        return idx;
      }
    }
    return -1;
  },

  /** Returns the string value of the given input */
  getInputValue: (event: InputEvent) =>
    (event.target as HTMLInputElement).value || '',

  /** Determines whether the passed value is an `Object` */
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  isObject: (value: any) =>
    value && typeof value === 'object' && value.constructor === Object,

  /** Determines whether the passed value is an array of objects */
  isArrayOfObjects: <T>(value: T) =>
    Array.isArray(value) && value.includes(MiscUtils.isObject),

  /** Logs the differences between two objects (for debugging) */
  logDifference: (label: string, objA: {}, objB: {}) => {
    const changed = MiscUtils.getDiffering(objA, objB);

    if (changed.length) {
      console.info(`${label} changed: [${changed.join(', ')}]`);
    }
  },

  /** Execute function with parameter on call */
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  execute: (fn: Function, ...value: any) => () => fn(...value),

  /** Execute function providing value from event */
  executeWithEventValue: (fn: Function) => (
    event: React.SyntheticEvent<HTMLSelectElement | HTMLInputElement>,
  ) => fn(event.currentTarget.value),

  /** Returns the given string value */
  identity: <T>(value: T) => value,

  /** Typeguard for synthetic events */
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  isSyntheticEvent: (event: any): event is React.SyntheticEvent<any> =>
    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    (event as React.SyntheticEvent<any>).nativeEvent !== undefined,

  /** No-op function; does nothing when executed */
  noop: () => null,

  preventDefault: (event: React.SyntheticEvent<HTMLElement>) =>
    event.preventDefault(),

  /** Return 0 in place of negative numbers */
  preventNegative: (value: number) => Math.max(value, 0),

  /** Parse the given Query string and return key value object */
  parseQueryString(searchString: string): QueryString {
    return searchString
      ? decodeURI(searchString)
          .substr(1)
          .split('&')
          .reduce((obj, query) => {
            const [key, value] = query.split('=');
            if (key === RESOURCE_ID_FILTER) {
              obj[key] = value.split(',');
            } else {
              obj[key] =
                value === 'true' ? true : value === 'false' ? false : value;
            }
            return obj;
          }, {})
      : {};
  },

  /** Sets the value on an object at the given path */
  set: <T>(obj: {}, path: string, value: T, schema: JSONSchema6) => {
    const parts = path.split('.');
    let part;
    let ref = obj;

    while ((part = parts.shift())) {
      /*
       * Since 'items' is not an actual piece of formdata
       * and it's a jsonReactSchema key, we are skiping it
       */
      if (part === 'items') {
        continue;
      } else {
        // Ensure key exists
        if (!ref.hasOwnProperty(part)) {
          /*
           * Checking if the type for the key is array, then create an Array
           * otherwise we create an Object
           */
          if (
            (BpitUtils.getDeepProperty(schema, part || '') as HasType).type ===
            'array'
          ) {
            ref[part] = [];
          } else {
            ref[part] = {};
          }
        }

        // Check if there is no more key left
        if (!parts.length) {
          if (Array.isArray(ref)) {
            ref.push({ [part]: value });
          } else {
            ref[part] = value;
          }
          continue;
        }
      }
      ref = ref[part];
    }

    return obj;
  },

  /** Convert YYYY-MM-DD date string to UTC shifted */
  shortDateToIso: (value: string) => moment(value).toISOString(),

  /** Create a keyed sorting callback */
  sortBy: <T>(key: string) => (a: T, b: T) =>
    a[key].toLowerCase().trim() > b[key].toLowerCase().trim() ? 1 : -1,

  /**
   * Create a keyed sorting callback
   *  conditional to shift other subcategory to the end of list
   */
  sortSubCategories: <T>(key: string) => (a: T, b: T) => {
    if (a[key] === 'Other') {
      return 1;
    } else {
      return a[key].toLowerCase().trim() > b[key].toLowerCase().trim() ? 1 : -1;
    }
  },

  stopPropagation: (event: React.SyntheticEvent<HTMLElement>) => {
    event.stopPropagation();
    event.nativeEvent.stopImmediatePropagation();
  },

  /** Returns string with all whitespace stripped */
  stripWhiteSpace: (value: string) => value.replace(rxWhitespace, ''),

  /** Returns true */
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  stubTrue: (...args: any[]) => true,

  /** Returns string in lowercase with spaces replaced by underscores */
  toSnakecase: (value: string) =>
    value.toLowerCase().replace(rxWhitespace, '_'),

  /** Returns string in lowercase with spaces replacing underscores */
  toNormalCase: (value: string) =>
    value.toLowerCase().replace(new RegExp(/_/g), ' '),
  /** Generate a UUID */
  uuid: () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(
      /[xy]/g,
      (char: string) => {
        const r = Math.floor(Math.random() * 16);

        if (char === 'x') {
          return r.toString(16);
        }

        /* eslint-disable-next-line */
        return ((r & 0x3) | 0x8).toString(16);
      },
    );
  },
  /** sets query value to the template fields */
  interpolateMessage(template: ReachTemplate, queryString: QueryString) {
    const { max, ramMax, region, sataMax, serverMax, ssdMax } = queryString;
    return (template.message as string[])
      .join('\n')
      .replace('{$max}', max || '')
      .replace('{$ramMax}', ramMax || '')
      .replace('{$region}', region || '')
      .replace('{$sataMax}', sataMax || '')
      .replace('{$serverMax}', serverMax || '')
      .replace('{$ssdMax}', ssdMax || '');
  },

  /** Check if a webcomponent field is disabled */
  isFieldDisabled: (isDisabled: boolean | Boolean | undefined) =>
    isDisabled && { disabled: true },

  /** Check if a webcomponent checkbox is checked */
  isCheckboxChecked: (isChecked: boolean | undefined) =>
    isChecked && { checked: true },

  /** Returns preffered browser language, default to english */

  browserLanguage: (navigator: NavigatorLanguages) => {
    if (navigator.languages) {
      // Supports for chrome ,firefox;
      return navigator.languages[0];
    } else {
      // Supports for IE-all versions
      return navigator.language || navigator.userLanguage || 'en';
    }
  },
  /** Returns true if japanese is browser preffered language */

  isJapaneseIncludedInBrowser: (navigator: NavigatorLanguages) => {
    if (navigator.languages) {
      // Supports for chrome ,firefox;
      return navigator.languages[0] === 'ja';
    } else {
      // Supports for IE-all versions
      return navigator.language === 'ja' || navigator.userLanguage === 'ja';
    }
  },

  getFormat: () => {
    return moment()
      .localeData()
      .longDateFormat('L');
  },

  /** Convert date to CST */
  isCESReleased: () => {
    const dateRelease = new Date('10/31/2022');
    const today = new Date();
    const dateString = today.toLocaleDateString('en-US', {
      timeZone: 'America/Chicago',
    });
    const currentDate = new Date(dateString);
    return currentDate > dateRelease;
  },

  // changes dateString to locale format
  changeDateToLoacleFormat(dateString?: string) {
    return dateString ? moment(dateString).format(this.getFormat()) : '';
  },

  areTwoArrayEqual: <T>(arrayOne: T[], arrayTwo: T[]) => {
    return (
      arrayOne.length === arrayTwo.length &&
      !arrayOne.some((element: T) => arrayTwo.indexOf(element) === -1)
    );
  },

  isUserImpersonated: () => {
    const pilotNavElement = document.getElementsByTagName('pilot-global-nav');
    const authenticatedby =
      pilotNavElement &&
      pilotNavElement[0] &&
      pilotNavElement[0].getAttribute('authenticatedby');

    return authenticatedby ? authenticatedby.includes('IMPERSONATE') : false;
  },

  logSentryErrorManually: (data: SentryManualError) => {
    const errorObject = new VError(
      {
        name: data.name,
      },
      data.description,
    );

    Sentry.withScope((scope) => {
      if (data.extraData) {
        scope.setExtra('data', data.extraData);
      }
      if (data.tags) {
        scope.setTags(data.tags);
      }
      if (data.fingerprint) {
        scope.setFingerprint(data.fingerprint);
      }

      Sentry.captureException(errorObject);
    });
  },

  includesAny: (values: string[], updatedValue: string) =>
    values.some((valueData) => updatedValue.includes(valueData)),

  addRemoveFiltersToUrl: (action: string, key: string, value?: string) => {
    const params = new URLSearchParams(window.location.search);
    if (action === ADD_PARAM && value) {
      params.set(key, value);
    } else {
      params.delete(key);
    }
    // @ts-ignore
    const newSearch = Array.from(params.keys())
      .map((param: string) => `${param}=${params.get(param)}`)
      .join('&');
    return `${window.location.pathname}?${newSearch}`;
  },
};

export { MiscUtils };
